/**
 * Utility functions for handling referral codes from URL query parameters
 */

const REFERRAL_CODE_STORAGE_KEY = 'eclipsera_referral_code'
/** Set when code was captured from `?ref=` / `?referral=` so signup UIs can lock the field. */
const REFERRAL_LOCKED_FROM_URL_KEY = 'eclipsera_referral_locked_from_url'

/**
 * Extract referral code from URL query parameters
 * Supports both 'ref' and 'referral' query parameters
 * @returns The referral code if found, null otherwise
 */
export function extractReferralCodeFromURL(): string | null {
  if (typeof window === 'undefined') return null

  const urlParams = new URLSearchParams(window.location.search)
  // Check for both 'ref' and 'referral' query parameters
  const refCode = urlParams.get('ref') || urlParams.get('referral')
  
  if (refCode && refCode.trim().length > 0) {
    return refCode.trim()
  }
  
  return null
}

/**
 * Store referral code in localStorage
 * @param code The referral code to store
 */
export function storeReferralCode(code: string): void {
  if (typeof window === 'undefined') return
  
  try {
    localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, code)
  } catch (error) {
    console.error('Failed to store referral code:', error)
  }
}

/**
 * Get stored referral code from localStorage
 * @returns The stored referral code or null if not found
 */
export function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null
  
  try {
    return localStorage.getItem(REFERRAL_CODE_STORAGE_KEY)
  } catch (error) {
    console.error('Failed to get stored referral code:', error)
    return null
  }
}

/**
 * Clear stored referral code from localStorage
 */
export function clearStoredReferralCode(): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY)
    localStorage.removeItem(REFERRAL_LOCKED_FROM_URL_KEY)
  } catch (error) {
    console.error('Failed to clear referral code:', error)
  }
}

export function markReferralLockedFromUrl(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(REFERRAL_LOCKED_FROM_URL_KEY, '1')
  } catch (error) {
    console.error('Failed to mark referral locked:', error)
  }
}

/** True if the stored referral came from an invite link (query param), not manual entry. */
export function isReferralLockedFromUrl(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(REFERRAL_LOCKED_FROM_URL_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Initialize referral code from URL and store it
 * This should be called when the app loads
 * @returns The referral code if found and stored, null otherwise
 */
export function initializeReferralCode(): string | null {
  // First, try to get from URL
  const urlCode = extractReferralCodeFromURL()
  
  if (urlCode) {
    // Store the code from URL (this takes priority)
    storeReferralCode(urlCode)
    markReferralLockedFromUrl()
    return urlCode
  }
  
  // If no code in URL, check if we have a stored one
  const storedCode = getStoredReferralCode()
  return storedCode
}

/**
 * Get the current referral code (from URL or storage)
 * @returns The referral code or null
 */
export function getReferralCode(): string | null {
  // First check URL (takes priority)
  const urlCode = extractReferralCodeFromURL()
  if (urlCode) {
    return urlCode
  }
  
  // Then check storage
  return getStoredReferralCode()
}

